import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getAdminSubmissions } from '../../api/client';
import { STYLES, StyleKey } from '../../data/styles';
import { AdminSubmissionItem } from '../../types';

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function StyleBadge({ styleKey }: { styleKey: StyleKey }) {
  const style = STYLES[styleKey];
  return (
    <span
      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
      style={{ backgroundColor: style.lightColor, color: style.darkColor }}
    >
      {style.name}
    </span>
  );
}

function exportCsv(items: AdminSubmissionItem[]) {
  const headers = ['Name', 'Email', 'Submitted At', 'Dominant Style', 'Backup Style', 'Driver', 'Expressive', 'Amiable', 'Analytic'];
  const rows = items.map((item) => [
    `"${item.name.replace(/"/g, '""')}"`,
    `"${item.email.replace(/"/g, '""')}"`,
    `"${formatDate(item.submittedAt)}"`,
    item.dominantStyle,
    item.backupStyle,
    item.scores.driver,
    item.scores.expressive,
    item.scores.amiable,
    item.scores.analytic,
  ]);
  const csv = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `style-quiz-submissions-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export function AdminDashboard() {
  const [items, setItems] = useState<AdminSubmissionItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const pageSize = 50;
  const totalPages = Math.ceil(totalCount / pageSize);

  const fetchPage = useCallback((p: number) => {
    setLoading(true);
    setPage(p);
  }, []);

  useEffect(() => {
    if (!loading) return;
    getAdminSubmissions(page, pageSize)
      .then((data) => {
        setItems(data.items);
        setTotalCount(data.totalCount);
        setError(null);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Failed to load submissions');
      })
      .finally(() => setLoading(false));
  }, [loading, page]);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-brand">Submissions</h1>
          {!loading && (
            <p className="text-sm text-brand-slate mt-1">{totalCount} total responses</p>
          )}
        </div>
        <button
          onClick={() => exportCsv(items)}
          disabled={items.length === 0}
          className="
            inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-brand/20
            text-sm font-medium text-brand
            hover:bg-porcelain disabled:opacity-50 disabled:cursor-not-allowed
            transition-colors
          "
        >
          Export CSV
        </button>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm mb-6">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-4 border-cream/40 border-t-scarlet rounded-full animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 text-brand-slate">
          <p className="text-lg">No submissions yet.</p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto rounded-xl border border-cream/40 shadow-sm">
            <table className="min-w-full divide-y divide-cream/40 bg-white">
              <thead className="bg-porcelain">
                <tr>
                  {['Name', 'Email', 'Date', 'Dominant Style', 'Backup Style', 'Actions'].map((h) => (
                    <th
                      key={h}
                      className={`px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-brand-slate${h === 'Actions' ? ' sr-only' : ''}`}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-cream/20">
                {items.map((item) => (
                  <tr key={item.id} className="hover:bg-porcelain transition-colors">
                    <td className="px-5 py-3 text-sm font-medium text-brand whitespace-nowrap">
                      {item.name}
                    </td>
                    <td className="px-5 py-3 text-sm text-brand-slate whitespace-nowrap">
                      {item.email}
                    </td>
                    <td className="px-5 py-3 text-sm text-brand-slate whitespace-nowrap">
                      {formatDate(item.submittedAt)}
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap">
                      <StyleBadge styleKey={item.dominantStyle} />
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap">
                      <StyleBadge styleKey={item.backupStyle} />
                    </td>
                    <td className="px-5 py-3 text-right whitespace-nowrap">
                      <Link
                        to={`/result/${item.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-scarlet hover:text-scarlet-dark font-medium"
                      >
                        View →
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between text-sm text-brand-slate">
              <span>
                Page {page} of {totalPages}
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => fetchPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  className="px-3 py-1.5 rounded border border-brand/20 hover:bg-porcelain disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Previous
                </button>
                <button
                  onClick={() => fetchPage(Math.min(totalPages, page + 1))}
                  disabled={page === totalPages}
                  className="px-3 py-1.5 rounded border border-brand/20 hover:bg-porcelain disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
